/*
 * Level Controller
*/

import Event from '../events';
import EventHandler from '../event-handler';
import { logger } from '../utils/logger';
import { ErrorTypes, ErrorDetails } from '../errors';
import BufferHelper from '../helper/buffer-helper';

class LevelController extends EventHandler {

	constructor(hls) {
		super(hls,
			Event.MANIFEST_LOADED,
			Event.LEVEL_LOADED,
			Event.FRAG_LOADED,
			Event.STREAM_ALL_LEVELS_LOADED,
			Event.ERROR
		);

		this.ontick = this.tick.bind(this);
		this._manualLevel = -1;
		this._allLevelsLoaded = false;
		this._pendingTimer = undefined;
		this._levelsFailed = 0;
		this._baseLevelFailed = undefined;
		this._timersActive = 0;
	}

	destroy() {
		this.stopLoad();
		this.clearTimer();

		EventHandler.prototype.destroy.call(this); //Mactrix line added, memory clean up
		this._manualLevel = -1;

		//memory clean up
		this._levels = null;
		this.ontick = null;

		//this.hls = null;
	}

	clearTimer() {
		if (this.timer) {
			clearTimeout(this.timer);
			this.timer = null;
			this._timersActive--;
		}
	}

	incrementTimerCount() {
		this._timersActive++;
		if(this._timersActive > 1) {
			logger.warn(`Timer leak in level controller, number of active timers: ${this._timersActive}`);
		}
	}

	startLoad() {
		this.canload = true;
		let levels = this._levels;

		// clean up live level details to force reload them, and reset load errors
		if(levels) {
			levels.forEach(level => {
				level.loadError = 0;
				const levelDetails = level.details;
				if (levelDetails && levelDetails.live && levels.length > 1) {
					level.details = undefined;
				}
			});
		}

		// speed up live playlist refresh if timer exists
		if (this.timer) {
			this.clearTimer();
			this.tick();
		}
	}

	stopLoad() {
	//Mactrix additions start
	//stop the tick when we stop loading
	//we only call stopLoad when we need to stop all functionality, so may as well halt timer here
		this.clearTimer();
	//Mactrix additions end

		this.canload = false;
	}

	//don't start reloading manifests until STREAM_ALL_LEVELS_LOADED
	onStreamAllLevelsLoaded() {
		this._allLevelsLoaded = true;
		if(this._pendingTimer) {
			this.clearTimer();
			this.timer = setTimeout(this.ontick, this._pendingTimer);
			this.incrementTimerCount();
		}
	}

	onManifestLoaded(data) {
		const hls = this.hls;

		// https://github.com/video-dev/hls.js/issues/332
		const brokenMp3InMp4 = hls.browChrome || hls.browFirefox;

		this._level = undefined; //Mactrix line added, reset level on new manifest

		// regroup redundant level together
		let bitrateSet = {};
		let levels0 = [];
		let videoCodecFound = false;
		let audioCodecFound = false;
		data.levels.forEach( level => {
			if(level.videoCodec) {
				videoCodecFound = true;
			}

			// erase audio codec info if browser does not support mp4a.40.34. demuxer will autodetect codec and fallback to mpeg/audio
			if(brokenMp3InMp4 && level.audioCodec && level.audioCodec.indexOf('mp4a.40.34') !== -1) {
				level.audioCodec = undefined;
			}

			if(level.audioCodec || (level.attrs && level.attrs.AUDIO)) {
				audioCodecFound = true;
			}

			let redundantLevelId = bitrateSet[level.bitrate];
			if (redundantLevelId === undefined) {
				bitrateSet[level.bitrate] = levels0.length;
				level.url = [level.url];
				level.urlId = 0;
				levels0.push(level);
			}
			else {
				levels0[redundantLevelId].url.push(level.url);
			}
		});

		// remove audio-only level if we also have levels with audio+video codecs signalled
		let levels = [];
		if(videoCodecFound && audioCodecFound) {
			levels0.forEach(level => {
				if(level.videoCodec) {
					levels.push(level);
				}
			});
		}
		else {
			levels = levels0;
		}

		// only keep level with supported audio/video codecs
		levels = levels.filter(function (level) {
			let audioCodec = level.audioCodec;
			let videoCodec = level.videoCodec;

			let checkSupported = function (type, codec) {
				return MediaSource.isTypeSupported(`${type}/mp4;codecs=${codec}`);
			};

			return (!audioCodec || checkSupported('audio', audioCodec)) &&
				(!videoCodec || checkSupported('video', videoCodec)
			);
		});

		if(levels.length) {
			// start bitrate is the first bitrate of the manifest
			const bitrateStart = levels[0].bitrate;

			// sort level on bitrate
			levels.sort(function (a, b) {
				return a.bitrate - b.bitrate;
			});
			this._levels = levels;

			// find index of first level in sorted levels
			for (let i = 0; i < levels.length; i++) {
				if (levels[i].bitrate === bitrateStart) {
					this._firstLevel = i;
					logger.log(`manifest loaded,${levels.length} level(s) found, first bitrate:${bitrateStart}`);
					break;
				}
			}

			hls.trigger(Event.MANIFEST_PARSED, {
				levels: levels,
				firstLevel: this._firstLevel,
				stats: data.stats,
				audio : audioCodecFound,
				video : videoCodecFound,
				altAudio : data.audioTracks.length > 0,
			});
		}
		else {
			hls.trigger(Event.ERROR, {
				type: ErrorTypes.MEDIA_ERROR,
				details: ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR,
				fatal: true,
				url: hls.url,
				reason: 'no level with compatible codecs found in manifest',
			});
		}
	}

	get levels() {
		return this._levels;
	}

	get level() {
		return this._level;
	}

	set level(newLevel) {
		let levels = this._levels;

		if (levels && levels.length > newLevel) {
			if (this._level !== newLevel || levels[newLevel].details === undefined) {
				this.setLevelInternal(newLevel);
			}
		}
	}

	setLevelInternal(newLevel) {
		const levels = this._levels;
		const hls = this.hls;
		// check if level idx is valid
		if (newLevel >= 0 && newLevel < levels.length) {
			// stopping live reloading timer if any
			this.clearTimer();
			if (this._level !== newLevel) {
				logger.log(`switching to level ${newLevel}`);
				this._level = newLevel;
				var levelProperties = levels[newLevel];
				levelProperties.level = newLevel;

				hls.trigger(Event.LEVEL_SWITCHING, levelProperties);
			}

			let level = levels[newLevel];
			let levelDetails = level.details;

			// check if we need to load playlist for this level

			//Mactrix line changed, prevent redundant reloads of single manifest live streams
			if (!levelDetails || (levelDetails.live === true && levels.length > 1)) {
				// level not retrieved yet, or live playlist we need to (re)load it
				const urlId = level.urlId;

				hls.trigger(Event.LEVEL_LOADING, {
					url: level.url[urlId],
					level: newLevel,
					id: urlId,
				});
			}
		}
		else {
			// invalid level id given, trigger error
			hls.trigger(Event.ERROR, {
				type : ErrorTypes.OTHER_ERROR,
				details: ErrorDetails.LEVEL_SWITCH_ERROR,
				level: newLevel,
				fatal: false,
				reason: 'invalid level idx',
			});
		}
	}

	get manualLevel() {
		return this._manualLevel;
	}

	set manualLevel(newLevel) {
		this._manualLevel = newLevel;
		if (this._startLevel === undefined) {
			this._startLevel = newLevel;
		}
		if (newLevel !== -1) {
			this.level = newLevel;
		}
	}

	get firstLevel() {
		return this._firstLevel;
	}

	set firstLevel(newLevel) {
		this._firstLevel = newLevel;
	}

	get startLevel() {
		// hls.startLevel takes precedence over config.startLevel
		// if none of these values are defined, fallback on this._firstLevel (first quality level appearing in variant manifest)
		if (this._startLevel === undefined) {
			let configStartLevel = this.hls.config.startLevel;
			if (configStartLevel !== undefined) {
				return configStartLevel;
			}
			else {
				return this._firstLevel;
			}
		}
		else {
			return this._startLevel;
		}
	}

	set startLevel(newLevel) {
		this._startLevel = newLevel;
	}

	onError(data) {
		if(data.fatal) {
			return;
		}

		const details = data.details;

		let levelId;
		let levelError = false;

	// try to recover not fatal errors
		switch(details) {
		case ErrorDetails.FRAG_LOAD_ERROR:
		case ErrorDetails.FRAG_LOAD_TIMEOUT:
		case ErrorDetails.FRAG_LOOP_LOADING_ERROR:
		case ErrorDetails.KEY_LOAD_ERROR:
		case ErrorDetails.KEY_LOAD_TIMEOUT:
			//Mactix, this error handler doesn't do anything for audio frags
			if(data.frag.type !== 'audio') {
				levelId = data.frag.level;
			}
			break;

		case ErrorDetails.LEVEL_LOAD_ERROR:
		case ErrorDetails.LEVEL_LOAD_TIMEOUT:
			levelId = data.context.level;
			levelError = true;
			break;

		case ErrorDetails.REMUX_ALLOC_ERROR:
			levelId = data.level;
			break;

		default:
			break;
		}

		/*
			try to switch to a redundant stream if any available.
			if no redundant stream available, emergency switch down (if in auto mode and current level not 0)
			otherwise, we cannot recover this network error ...
		 */
		if (levelId !== undefined) {

			let level = this._levels[levelId];

			if(!level.loadError) {
				level.loadError = 1;
			}
			else {
				level.loadError++;
			}

			// if any redundant streams available and if we haven't try them all (level.loadError is reseted on successful frag/level load.
			// if level.loadError reaches nbRedundantLevel it means that we tried them all, no hope => let's switch down
			const nbRedundantLevel = level.url.length;
			if (nbRedundantLevel > 1 && level.loadError < nbRedundantLevel) {
				level.urlId = (level.urlId + 1) % nbRedundantLevel;
				level.details = undefined;
				logger.warn(`level controller,${details} for level ${levelId}: switching to redundant stream id ${level.urlId}`);
			}
			else {
				const hls = this.hls;
				const media = hls.media;
				let mediaBuffered = media && BufferHelper.isBuffered(media, media.currentTime) && BufferHelper.isBuffered(media, media.currentTime + 0.5);

				if(this._levelsFailed === 0) {
					this._baseLevelFailed = levelId;
				}
				this._levelsFailed++;

				// we could try to recover if in auto mode and current level not lowest level (0)
				if (this.tryNextVariant(levelId, true)) {
					logger.warn(`level controller,${details}: switch variant and retry fragment`);
				}
				else if(this._manualLevel === -1 && details === (ErrorDetails.FRAG_LOAD_ERROR || details === ErrorDetails.FRAG_LOAD_TIMEOUT)
				&& this._levelsFailed > 1) {
					//we've tried too many times, turn the frag we've been trying to load into a gap
					if(data.frag) {
						logger.warn(`level controller,${details}: marking dead frag`);

						hls.streamController.addToDeadFragList(data.frag.sn);
					}
				}
				else if(level && level.details && level.details.live) {
					logger.warn(`level controller,${details} on live stream, discard`);
					if (levelError) {
						// reset this._level so that another call to set level() will retrigger a frag load
						this._level = undefined;
					}
					// other errors are handled by stream controller
				}
				else if (details === ErrorDetails.LEVEL_LOAD_ERROR || details === ErrorDetails.LEVEL_LOAD_TIMEOUT) {

					// 0.5 : tolerance needed as some browsers stalls playback before reaching buffered end
					if (mediaBuffered) {
						let retryDelay = hls.config.levelLoadingRetryDelay;
						logger.warn(`level controller,${details}, but media buffered, retry in ${retryDelay}ms`);
						this.clearTimer();
						this.timer = setTimeout(this.ontick, retryDelay);
						this.incrementTimerCount();
					}
					else {
						logger.error(`cannot recover ${details} error`);
						this._level = undefined;

						// stopping live reloading timer if any
						this.clearTimer();

						// switch error to fatal
						data.fatal = true;
					}
				}

				if(!data.fatal) {
					this.tick();
				}
			}
		}
	}

	tryNextVariant(levelId, calledByErrorHandler) {
		const hls = this.hls;
		const media = hls.media;
		let level = this._levels[levelId];
		let bufferInfo = { end: 0 };
		if(media) {
			bufferInfo = BufferHelper.bufferInfo(media, media.currentTime, 0);
		}

		if(this._baseLevelFailed === undefined) {
			this._baseLevelFailed = levelId;
		}

		const nbAllowedLevels = hls.maxAutoLevel - hls.minAutoLevel + 1;
		if (media && level.details && ((!calledByErrorHandler || level.details.live || bufferInfo.end - media.currentTime >= level.details.averagetargetduration * 2))
		&& this._manualLevel === -1 && this._levelsFailed < Math.min(nbAllowedLevels, hls.config.levelLoadingMaxRetry)) {
			let targetLevel = levelId;
			if(levelId <= this._baseLevelFailed) {
				//try to switch down
				targetLevel = levelId - 1;
				if(targetLevel < hls.minAutoLevel) {
					//if we have gone down as far as we can, return to the level we originally failed at and start going up
					targetLevel = Math.min(hls.maxAutoLevel, this._baseLevelFailed + 1);
				}
			}
			else {
				targetLevel = levelId + 1;
				if(targetLevel > hls.maxAutoLevel) {
					//if we have gone up as far as we can, return to the level we originally failed at
					//in general we shouldn't start going down after this - if being done by the error handler we'll have hit the error cap
					//and if being done by stream controller it'll stop switching variants once it realises it's back where it started
					targetLevel = Math.max(hls.minAutoLevel, this._baseLevelFailed);
				}
			}
			hls.nextAutoLevel = targetLevel;
			this.setLevelInternal(hls.nextAutoLevel);

			//if this was called by the error handler, the tick will happen later in the handler so we don't need to do it now
			if(!calledByErrorHandler) {
				this.tick();
			}

			return true;
		}
		else {
			//tell caller that we didn't switch variants
			return false;
		}
	}

  // reset level load error counter on successful frag loaded
	onFragLoaded(data) {
		const fragLoaded = data.frag;
		if (fragLoaded && fragLoaded.type === 'main') {
			const level = this._levels[fragLoaded.level];
			if (level) {
				level.loadError = 0;
			}
			this._levelsFailed = 0;
			this._baseLevelFailed = undefined;
		}
	}

	onLevelLoaded(data) {
		const levelId = data.level;

		// only process level loaded events matching with expected level
		if (levelId === this._level) {
			let curLevel = this._levels[levelId];
			//there's not really any point to resetting the error count until we know we can load frags from this level
			//curLevel.loadError = 0;

			let newDetails = data.details;
			// if current playlist is a live playlist, arm a timer to reload it
			if (newDetails.live) {
				let reloadInterval = 1000 * ( newDetails.averagetargetduration ? newDetails.averagetargetduration : newDetails.targetduration);
				let curDetails = curLevel.details;
				if (curDetails && newDetails.endSN === curDetails.endSN) {
					// follow HLS Spec, If the client reloads a Playlist file and finds that it has not
					// changed then it MUST wait for a period of one-half the target
					// duration before retrying.
					reloadInterval /= 2;
					logger.log(`same live playlist, reload twice faster`);
				}
				// decrement reloadInterval with level loading delay
				reloadInterval -= performance.now() - data.stats.trequest;
				// in any case, don't reload more than every second
				reloadInterval = Math.max(1000, Math.round(reloadInterval));
				logger.log(`live playlist, reload in ${reloadInterval} ms`);

				//Mactrix, don't start reloading manifests until STREAM_ALL_LEVELS_LOADED
				if(this._allLevelsLoaded) {
					this.clearTimer();
					this.timer = setTimeout(this.ontick, reloadInterval);
					this.incrementTimerCount();
				}
				else {
					logger.log('delaying arming timer until STREAM_ALL_LEVELS_LOADED');
					this._pendingTimer = reloadInterval;
				}
			}
			else {
				this.clearTimer();
			}
		}
	}

	tick() {
		const levelId = this._level;
		if (this._levels && levelId !== undefined && this.canload) {
			const level = this._levels[levelId];
			const urlId = level.urlId;

			this.hls.trigger(Event.LEVEL_LOADING, {
				url: level.url[urlId],
				level: levelId,
				id: urlId,
			});
		}
	}

	get nextLoadLevel() {
		if (this._manualLevel !== -1) {
			return this._manualLevel;
		}
		else {
			return this.hls.nextAutoLevel;
		}
	}

	set nextLoadLevel(nextLevel) {
		this.level = nextLevel;
		if (this._manualLevel === -1) {
			this.hls.nextAutoLevel = nextLevel;
		}
	}

//Mactrix addition start

	chunkIDAtTime(time) {
		// HlsJs doesn't load in the fragment data for a level until it switches to that level, meaning that if called
		// too quickly after a level switch checking the current level might not work. So we try the current level first,
		// and if that doesn't work, then try every level in sequence
		const levels = this.levels;
		const level = this.level;

		if(levels && level) {
			const details = levels[level].details;
			if(details) {
				const fragments = details.fragments;
				const fragCount = fragments.length;

				for(let i = 0; i < fragCount; i++) {
					const fragment = fragments[i];

					if (time > fragment.start && time < fragment.start + fragment.duration) {
						return i;
					}
				}
			}
		}

		const levelsCount = levels.length;
		for( let i = 0; i < levelsCount; i++) {
			const details = levels[i].details;

			if(details) {
				const fragments = details.fragments;
				const fragCount = fragments.length;

				for(let j = 0; j < fragCount; j++) {
					const fragment = fragments[j];

					if (time > fragment.start && time < fragment.start + fragment.duration) {
						return j;
					}
				}
			}
		}

		// if all else fails, return -1
		return -1;
	}

	endOfChunk(chunkID) {
		const levels = this.levels;
		let details = levels[this.level].details;

		try {
			if(details) {
				let fragment;

				if(chunkID > details.fragments.length) {
					fragment = details.fragments[details.fragments.length - 1];
				}
				else {
					fragment = details.fragments[chunkID];
				}

				return fragment.start + fragment.duration;
			}

			const levelsCount = levels.length;
			for(let i = 0; i < levelsCount; i++) {
				details = levels[i].details;

				if(details) {
					let fragment;

					if(chunkID > details.fragments.length) {
						fragment = details.fragments[details.fragments.length - 1];
					}
					else {
						fragment = details.fragments[chunkID];
					}

					return fragment.start + fragment.duration;
				}
			}
			return 0;
		}
		catch(err) {
			return 0;
		}
	}

//Mactrix addition end
}



export default LevelController;



// WEBPACK FOOTER //
// ./src/hlsjs/controller/level-controller.js